import React, { useState, useEffect, useRef } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import { useLocation } from "react-router-dom";
import api from "../../services/api";
import SpecialtyCakesDesktopView from "./SpecialtyCakesDesktopView";
import SpecialtyCakesMobileView from "./SpecialtyCakesMobileView";
import { useNavbarContext } from "../../context/NavbarContext";
import { useAppConfig } from "../../context/AppConfigContext";

const SpecialtyCakesPage = () => {
  const theme = useTheme();
  const { setScrollControlledNavbar, showNavbar } = useNavbarContext();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const [cakes, setCakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    recipient: "All",
    eventType: "All",
    decor: [],
    style: "",
  });
  const [sortOption, setSortOption] = useState("popularity");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const cakesPerPage = 12;
  const gridRef = useRef(null);
  const { config } = useAppConfig();
  const eventTypes = config?.eventTypes || [];
  const [attributeOptions, setAttributeOptions] = useState({
    decor: [],
    style: [],
    texture: [],
  });

  // ✅ Read query params
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const eventParam = queryParams.get("event");

    if (eventParam) {
      setFilters((prev) => ({
        ...prev,
        eventType: eventParam,
      }));
      setCurrentPage(1);
    }
    // You can extend here for other query filters like recipient, decor, style
  }, [location.search]);

  useEffect(() => {
    document.title = "Specialty Cakes";
  }, []);

  useEffect(() => {
    fetchCakes();
  }, [filters, sortOption, currentPage, searchQuery]);

  useEffect(() => {
    setScrollControlledNavbar(true);
    return () => {
      setScrollControlledNavbar(false);
    };
  }, [setScrollControlledNavbar]);

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const [decorRes, styleRes, textureRes] = await Promise.all([
          api.get("/cake-attributes/decor"),
          api.get("/cake-attributes/style"),
          api.get("/cake-attributes/texture"),
        ]);

        setAttributeOptions({
          decor: decorRes.data,
          style: styleRes.data,
          texture: textureRes.data,
        });
      } catch (error) {
        console.error("Error fetching attribute options:", error);
      }
    };

    fetchAttributes();
  }, []);

  const fetchCakes = async (overrideSearch = "") => {
    setLoading(true);
    try {
      const response = await api.get("/specialty-cakes", {
        params: {
          recipient:
            filters.recipient !== "All" ? filters.recipient : undefined,
          eventType:
            filters.eventType !== "All" ? filters.eventType : undefined,
          decor: filters.decor.length ? filters.decor.join(",") : undefined,
          style: filters.style !== "" ? filters.style : undefined,
          sortBy: sortOption,
          search: overrideSearch || searchQuery || undefined,
          page: currentPage,
          limit: cakesPerPage,
        },
      });
      setCakes(response.data);
      if (window.fbq && response.data?.length > 0) {
        response.data.forEach((cake) => {
          window.fbq("track", "ViewContent", {
            content_name: cake.displayName,
            content_ids: [cake._id],
            content_type: "product",
            value: cake.basePrice,
            currency: "CAD",
          });
        });
      }

      if (window.gtag && response.data?.length > 0) {
        window.gtag("event", "view_item_list", {
          item_list_name: "Specialty Cakes",
          currency: "CAD",
          items: response.data.map((cake, index) => ({
            item_id: cake._id,
            item_name: cake.displayName,
            item_category: cake.category || "specialtyCake",
            price: cake.basePrice,
            index,
          })),
        });
      }
    } catch (error) {
      console.error("Error fetching cakes:", error);
    } finally {
      setLoading(false);
    }
  };

  const getEventTypeLabel = (key) => {
    return eventTypes.find((et) => et.key === key)?.label || key;
  };

  const clearFilters = () => {
    setFilters({
      recipient: "All",
      eventType: "All",
      decor: [],
      style: "",
    });
    if (window.fbq) {
      window.fbq("trackCustom", "FiltersCleared");
    }
    if (window.gtag) {
      window.gtag("event", "filters_cleared", {
        page: "Specialty Cakes",
      });
    }
  };

  const removeFilter = (key, value) => {
    if (key === "decor" && value) {
      setFilters((prev) => ({
        ...prev,
        decor: prev.decor.filter((id) => id !== value),
      }));
    } else if (key === "decor") {
      setFilters((prev) => ({
        ...prev,
        decor: [],
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        [key]: "All",
      }));
    }
  };

  const clearSort = () => {
    setSortOption("popularity");
  };

  const applyPendingFilters = (pending) => {
    setFilters(pending);
    setCurrentPage(1);

    // ✅ Track filter usage
    if (window.fbq) {
      const selectedDecor = (pending.decor || []).join(", ") || "None";
      const selectedStyle = pending.style || "All";
      const selectedRecipient = pending.recipient || "All";
      const selectedEventType = pending.eventType || "All";

      window.fbq("trackCustom", "FilterUsed", {
        recipient: selectedRecipient,
        eventType: selectedEventType,
        decor: selectedDecor,
        style: selectedStyle,
      });
    }

    if (window.gtag) {
      window.gtag("event", "filter_used", {
        recipient: pending.recipient || "All",
        eventType: pending.eventType || "All",
        decor: (pending.decor || []).join(", ") || "None",
        style: pending.style || "All",
      });
    }
  };

  const handlePageChange = (value) => {
    setCurrentPage(value);
    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const sharedProps = {
    cakes,
    loading,
    filters,
    setFilters,
    clearFilters,
    removeFilter,
    sortOption,
    setSortOption,
    clearSort,
    searchQuery,
    setSearchQuery,
    fetchCakes,
    getEventTypeLabel,
    currentPage,
    handlePageChange,
    showNavbar,
    applyPendingFilters,
    attributeOptions,
  };

  return (
    <>
      {isMobile ? (
        <SpecialtyCakesMobileView {...sharedProps} />
      ) : (
        <SpecialtyCakesDesktopView {...sharedProps} gridRef={gridRef} />
      )}
    </>
  );
};

export default SpecialtyCakesPage;
